import { Box } from "@mui/material";
import { Card, Typography, Button } from "@powerdiary/components-library/lib";
import { X } from "lucide-react";
import { useMemo, useCallback } from "react";
import styles from "./Summary.module.css";
import SummarySteps, { IStep } from "./SummarySteps/SummarySteps";
import { useClientPortalConfigContext } from "../../../../context";
import WaitlistCard from "./WaitlistCard";
import {
    selectBooking,
    selectBookingType,
    useClientPortalSelector,
    useClientPortalDispatch,
    setCancelModalOpen,
} from "../../../../redux";
import {
    STEP_LOCATION,
    STEP_SERVICE,
    STEP_PROVIDER,
    STEP_AVAILABILITY,
    STEP_REGISTER,
    StepId,
} from "../../bookingSlice.types";
import { BookingType } from "types/appointmentBooking";
import { useBookingNavigation } from "../../hooks/useBookingNavigation";

const getStepNamesAndValues = (): Array<IStep> => {
    return [
        { name: "Location", value: STEP_LOCATION },
        { name: "Service", value: STEP_SERVICE },
        { name: "Provider", value: STEP_PROVIDER },
        { name: "Date & Time", value: STEP_AVAILABILITY },
        { name: "Details", value: STEP_REGISTER },
    ];
};

export default function Summary() {
    const booking = useClientPortalSelector(selectBooking);
    const bookingType = useClientPortalSelector(selectBookingType);
    const dispatch = useClientPortalDispatch();
    const stepKeys = getStepNamesAndValues();
    const {
        clientPortalConfig: { featureAccess },
    } = useClientPortalConfigContext();
    const { effectiveCurrentStep, handleGoToStep } = useBookingNavigation();

    const handleStepClick = useCallback(
        (step: StepId) => {
            handleGoToStep(step);
        },
        [handleGoToStep]
    );

    const handleCancel = () => {
        dispatch(setCancelModalOpen(true));
    };

    const getDisplayText = useCallback(
        (stepId: StepId): string | null => {
            switch (stepId) {
                case STEP_LOCATION:
                    return booking.location.data?.locationName || null;
                case STEP_SERVICE:
                    return booking.service.data?.serviceName || null;
                case STEP_PROVIDER:
                    return booking.provider.data?.providerName || null;
                case STEP_AVAILABILITY:
                    return booking.availability.data?.displayText || null;
                default:
                    return null;
            }
        },
        [booking]
    );

    const hasAnySteps = useMemo(() => {
        return getStepNamesAndValues().some((step) => {
            const displayText = getDisplayText(step.value);
            return displayText && displayText !== "See availability for all Providers" && displayText !== "Logged in";
        });
    }, [getDisplayText]);

    const shouldShowGroup = bookingType === BookingType.CLASS && booking.group.data?.groupName;
    const shouldShowWaitlistCard =
        (featureAccess?.canRequestWaitlist ?? false) && effectiveCurrentStep === STEP_AVAILABILITY;
    const hasAnyContent = hasAnySteps || shouldShowGroup;

    return (
        <Card className={styles.wrapper} data-test-id="section_summary">
            <Typography variant="paragraphLargeBold" className={styles.title} data-test-id="text_summary_title">
                Appointment summary
            </Typography>

            {hasAnyContent && (
                <>
                    <Box mt={4}>
                        <SummarySteps
                            handleStepClick={handleStepClick}
                            steps={stepKeys}
                            getDisplayText={getDisplayText}
                        />
                    </Box>
                    <Box className={styles.cancelButton}>
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<X size={14} strokeWidth={2} aria-hidden />}
                            size="extraSmall"
                            sx={{ paddingLeft: "4px" }}
                            onClick={handleCancel}
                            data-test-id="button_cancel_appointment_booking"
                        >
                            CANCEL
                        </Button>
                    </Box>
                </>
            )}
            {shouldShowWaitlistCard && <WaitlistCard />}
        </Card>
    );
}
